import type { fetchWithAuth } from "@/lib/auth";

export const OPEN_DEPOSITS_PATH = "/api/v1/wallet/deposits/open";

export const OPEN_DEPOSIT_CONTINUE_BY_PI_PATH = "/api/v1/wallet/deposits/continue-by-pi";

export const OPEN_DEPOSITS_REFRESH_EVENT = "wallet:open-deposits-refresh";

export const STRIPE_DEPOSIT_RETURN_STASH_KEY = "mot.wallet.stripeDepositReturn";

/** Discard Stripe return stash entries older than this (tab reloads / delayed retries). */
export const STRIPE_DEPOSIT_RETURN_STASH_TTL_MS = 10 * 60 * 1000;

export type StripeDepositReturnStash = {
  paymentIntentId: string | null;
  redirectStatus: string | null;
  /** Epoch ms when the stash was written; older entries are ignored. */
  storedAt?: number;
  /**
   * Wallet balance known to predate Stripe confirmation / redirect.
   * Used as the credit-poll baseline; must not be overwritten with a post-credit refresh.
   */
  balanceCentsBefore?: number | null;
  /** Deposit amount at confirm time (optional target helper for polling). */
  amountCents?: number | null;
};

/** Extract `pi_…` / `seti_…` id from a Stripe client secret (`pi_xxx_secret_yyy`). */
export function paymentIntentIdFromClientSecret(clientSecret: string): string | null {
  const match = /^(pi|seti)_[A-Za-z0-9]+/.exec(clientSecret);
  return match?.[0] ?? null;
}

/** 404/409 on continue means the deposit is no longer open (credited or terminal). */
export function isDepositContinueTerminalHttpStatus(
  status: number | undefined,
  errorCode?: string,
): boolean {
  if (status === 404) return true;
  if (status === 409) return errorCode !== "WALLET_FROZEN";
  return false;
}

/** Refresh wallet summary after a write the UI must not keep treating as enabled. */
export function shouldRefreshWalletAfterWriteFailure(
  status?: number,
  errorCode?: string,
): boolean {
  return errorCode === "WALLET_FROZEN" || isDepositContinueTerminalHttpStatus(status, errorCode);
}

export type ContinueDepositResult = {
  paymentIntentId: string;
  clientSecret: string;
  amountCents: number;
};

export type OpenWalletDeposit = {
  id: string;
  amountCents: number;
  status:
    | "pending"
    | "failed"
    | "refunded"
    | "partially_refunded"
    | "disputed"
    | "partially_disputed";
  createdAt: string;
};

export function continueDepositPath(depositId: string): string {
  return `/api/v1/wallet/deposits/${encodeURIComponent(depositId)}/continue`;
}

export function readStripeDepositReturnStash(): StripeDepositReturnStash | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STRIPE_DEPOSIT_RETURN_STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StripeDepositReturnStash;
    if (
      (parsed.paymentIntentId !== null && typeof parsed.paymentIntentId !== "string") ||
      (parsed.redirectStatus !== null && typeof parsed.redirectStatus !== "string")
    ) {
      return null;
    }
    if (
      parsed.balanceCentsBefore != null &&
      (typeof parsed.balanceCentsBefore !== "number" || !Number.isFinite(parsed.balanceCentsBefore))
    ) {
      return null;
    }
    if (
      parsed.amountCents != null &&
      (typeof parsed.amountCents !== "number" || !Number.isFinite(parsed.amountCents))
    ) {
      return null;
    }
    if (typeof parsed.storedAt === "number" && Number.isFinite(parsed.storedAt)) {
      if (Date.now() - parsed.storedAt > STRIPE_DEPOSIT_RETURN_STASH_TTL_MS) {
        sessionStorage.removeItem(STRIPE_DEPOSIT_RETURN_STASH_KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeStripeDepositReturnStash(
  stash: Omit<StripeDepositReturnStash, "storedAt"> & { storedAt?: number },
): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    const payload: StripeDepositReturnStash = {
      paymentIntentId: stash.paymentIntentId,
      redirectStatus: stash.redirectStatus,
      balanceCentsBefore:
        stash.balanceCentsBefore === undefined ? undefined : stash.balanceCentsBefore,
      amountCents: stash.amountCents === undefined ? undefined : stash.amountCents,
      storedAt: stash.storedAt ?? Date.now(),
    };
    sessionStorage.setItem(STRIPE_DEPOSIT_RETURN_STASH_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / quota — continue without stash so URL cleanup still runs.
  }
}

/** Merge into any existing stash so pre-confirm balance survives the Stripe return write. */
export function mergeStripeDepositReturnStash(
  patch: Partial<Omit<StripeDepositReturnStash, "storedAt">> & {
    paymentIntentId?: string | null;
    redirectStatus?: string | null;
  },
): void {
  const prev = readStripeDepositReturnStash();
  writeStripeDepositReturnStash({
    paymentIntentId:
      patch.paymentIntentId !== undefined ? patch.paymentIntentId : (prev?.paymentIntentId ?? null),
    redirectStatus:
      patch.redirectStatus !== undefined ? patch.redirectStatus : (prev?.redirectStatus ?? null),
    balanceCentsBefore:
      patch.balanceCentsBefore !== undefined
        ? patch.balanceCentsBefore
        : (prev?.balanceCentsBefore ?? null),
    amountCents:
      patch.amountCents !== undefined ? patch.amountCents : (prev?.amountCents ?? null),
  });
}

export function clearStripeDepositReturnStash(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(STRIPE_DEPOSIT_RETURN_STASH_KEY);
  } catch {
    // ignore
  }
}

function isOpenWalletDeposit(row: unknown): row is OpenWalletDeposit {
  if (!row || typeof row !== "object") return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.id === "string" &&
    r.id.length > 0 &&
    typeof r.amountCents === "number" &&
    Number.isFinite(r.amountCents) &&
    Number.isInteger(r.amountCents) &&
    (r.status === "pending" ||
      r.status === "failed" ||
      r.status === "refunded" ||
      r.status === "partially_refunded" ||
      r.status === "disputed" ||
      r.status === "partially_disputed") &&
    typeof r.createdAt === "string"
  );
}

/** Pure list normalizer for API `data` payloads (testable). */
export function normalizeOpenDeposits(data: unknown): OpenWalletDeposit[] {
  if (!data || typeof data !== "object") return [];
  const deposits = (data as { deposits?: unknown }).deposits;
  if (!Array.isArray(deposits)) return [];
  return deposits.filter(isOpenWalletDeposit);
}

export async function fetchOpenDeposits(
  fetchAuth: typeof fetchWithAuth,
): Promise<OpenWalletDeposit[]> {
  const res = await fetchAuth(OPEN_DEPOSITS_PATH);
  if (!res.ok) {
    throw new Error("Failed to load open deposits");
  }
  const json = (await res.json()) as {
    success?: boolean;
    data?: unknown;
  };
  if (!json.success) {
    return [];
  }
  return normalizeOpenDeposits(json.data);
}

async function postContinueDepositRequest(
  fetchAuth: typeof fetchWithAuth,
  path: string,
  body: Record<string, unknown>,
): Promise<ContinueDepositResult> {
  const res = await fetchAuth(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const responseBody = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errorPayload = (responseBody as { error?: { message?: string; code?: string } }).error;
    const err = new Error(errorPayload?.message ?? "Could not continue deposit") as Error & {
      httpStatus?: number;
      errorCode?: string;
    };
    err.httpStatus = res.status;
    if (typeof errorPayload?.code === "string") {
      err.errorCode = errorPayload.code;
    }
    throw err;
  }
  const data = (
    responseBody as {
      data?: { paymentIntentId?: string; clientSecret?: string; amountCents?: number };
    }
  ).data;
  const paymentIntentId = data?.paymentIntentId;
  const clientSecret = data?.clientSecret;
  const amountCents = data?.amountCents;
  if (
    !paymentIntentId ||
    !clientSecret ||
    typeof amountCents !== "number" ||
    !Number.isFinite(amountCents)
  ) {
    throw new Error("Invalid continue response");
  }
  return { paymentIntentId, clientSecret, amountCents };
}

export async function continueDeposit(
  fetchAuth: typeof fetchWithAuth,
  depositId: string,
): Promise<ContinueDepositResult> {
  return postContinueDepositRequest(fetchAuth, continueDepositPath(depositId), {});
}

export function parseStripeDepositReturnParams(params: URLSearchParams): {
  paymentIntentId: string | null;
  redirectStatus: string | null;
} {
  return {
    paymentIntentId: params.get("payment_intent"),
    redirectStatus: params.get("redirect_status"),
  };
}

export function stripStripeDepositReturnParams(url: URL): URL {
  const next = new URL(url.href);
  next.searchParams.delete("payment_intent");
  next.searchParams.delete("payment_intent_client_secret");
  next.searchParams.delete("redirect_status");
  return next;
}

export async function continueDepositByPaymentIntent(
  fetchAuth: typeof fetchWithAuth,
  paymentIntentId: string,
): Promise<ContinueDepositResult> {
  return postContinueDepositRequest(fetchAuth, OPEN_DEPOSIT_CONTINUE_BY_PI_PATH, {
    paymentIntentId,
  });
}
